import {skipToken} from '@reduxjs/toolkit/query';
import {Card, Form, FormField, FormSubmitHandler, showNotification} from 'platform/components';
import {Show, Space, VStack} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';

import {useGetTelephonyLineQuery, useUpdateTelephonyLineMutation} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, queryParams, useBranches, usePermissions} from '@dms/shared';

import {ApiError, useNavigate, useQueryState} from 'shared/web';

import {TelephonyLineBusinessHoursCard} from './components/TelephonyLineBusinessHoursCard';
import {TelephonyLineGreetingsCard} from './components/TelephonyLineGreetingsCard';
import {TelephonyLineNumberCard} from './components/TelephonyLineNumberCard';
import {TelephonyLineRecordingConsentCard} from './components/TelephonyLineRecordingConsentCard';
import {TelephonyLineRoutingTab} from './components/TelephonyLineRoutingTab';
import {
  getTelephonyLineFormValues,
  getUpdateTelephonyLineRequestBody,
  telephonyLineFormSchema,
  TelephonyLineFormValues,
} from './utils/telephonyLineForm';

const ROUTING_TAB_ID = 'routing';

export function TelephonyLineDetail() {
  const {lineId} = useParams();
  const navigate = useNavigate();
  const [, setTabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);

  const {
    data: line,
    isLoading,
    isError,
  } = useGetTelephonyLineQuery(isNotNil(lineId) ? {lineId} : skipToken);

  const {branchOptions, isLoading: isBranchListLoading} = useBranches();

  const [updateLine] = useUpdateTelephonyLineMutation();

  const [canWriteTelephonySettings] = usePermissions({
    permissionKeys: ['telephonySettingsWrite'],
  });

  const isReadOnly = !canWriteTelephonySettings;

  const handleSubmit: FormSubmitHandler<TelephonyLineFormValues> = async (
    values,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    if (isNil(lineId)) {
      return;
    }

    await updateLine({lineId, body: getUpdateTelephonyLineRequestBody(values)})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));

        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }

        navigate(settingsRoutes.telephony);
      })
      .catch((error: ApiError) =>
        handleApiError(error, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const lineSettingsTab = (
    <SettingsSection>
      <VStack spacing={4}>
        <Form<TelephonyLineFormValues>
          // Remounts the form on a parameter-only navigation, where defaultValues alone
          // would keep the previously loaded line's values.
          key={line?.id}
          experimentalZodSchema={telephonyLineFormSchema}
          onSubmit={handleSubmit}
          defaultValues={isNotNil(line) ? getTelephonyLineFormValues(line) : undefined}
          shouldWatchForUnsavedChanges
        >
          {(control, formApi, navigateWithoutBlocker) => (
            <VStack spacing={4}>
              <Card title={i18n.t('page.settings.telephony.lineCardTitle')}>
                <VStack spacing={4}>
                  <FormField
                    control={control}
                    name="name"
                    type="text"
                    label={i18n.t('general.labels.name')}
                    isRequired
                    isDisabled={isReadOnly}
                    data-testid={testIds.settings.telephonyLineDetail('name')}
                  />
                  <FormField
                    control={control}
                    name="branchId"
                    type="choice"
                    label={i18n.t('entity.branch.labels.branch')}
                    options={branchOptions}
                    isLoading={isBranchListLoading}
                    isDisabled={isReadOnly}
                    data-testid={testIds.settings.telephonyLineDetail('branchId')}
                  />
                </VStack>
              </Card>
              <TelephonyLineBusinessHoursCard
                control={control}
                formApi={formApi}
                isReadOnly={isReadOnly}
                data-testid={testIds.settings.telephonyLineDetail('hours')}
              />
              <TelephonyLineGreetingsCard
                control={control}
                isReadOnly={isReadOnly}
                hasCustomRouting={line?.routing.isDefault === false}
                onOpenRouting={() => setTabId(ROUTING_TAB_ID)}
                data-testid={testIds.settings.telephonyLineDetail('greetings')}
              />
              <TelephonyLineRecordingConsentCard
                line={line}
                isReadOnly={isReadOnly}
                data-testid={testIds.settings.telephonyLineDetail('recording')}
              />
              <TelephonyLineNumberCard
                line={line}
                isReadOnly={isReadOnly}
                data-testid={testIds.settings.telephonyLineDetail('numbers')}
              />
              <Show when={canWriteTelephonySettings}>
                <Space vertical={18} />
                <SettingsFooter
                  actions={[
                    {
                      type: 'button',
                      title: i18n.t('general.actions.discardChanges'),
                      variant: 'secondary',
                      onClick: () => navigateWithoutBlocker(settingsRoutes.telephony),
                      'data-testid': testIds.settings.telephonyLineDetail('discard'),
                    },
                    {
                      type: 'form-button',
                      control,
                      title: i18n.t('general.actions.saveChanges'),
                      buttonType: 'submit',
                      variant: 'primary',
                      'data-testid': testIds.settings.telephonyLineDetail('save'),
                    },
                  ]}
                />
              </Show>
            </VStack>
          )}
        </Form>
      </VStack>
    </SettingsSection>
  );

  return (
    <SettingsTemplate
      header={{
        title: line?.name,
        breadcrumbs: [
          {label: i18n.t('page.settings.telephony.title')},
          {
            label: i18n.t('page.settings.telephony.linesTitle'),
            href: settingsRoutes.telephony,
          },
        ],
      }}
      isLoading={isLoading}
      isError={isError}
      data-testid={testIds.settings.telephonyLineDetail('settingsTemplate')}
      tabs={[
        {
          queryId: 'general',
          title: i18n.t('page.settings.telephony.lineSettingsTab'),
          content: <Show when={isNotNil(line)}>{lineSettingsTab}</Show>,
        },
        {
          queryId: ROUTING_TAB_ID,
          title: i18n.t('page.settings.telephony.routingTab'),
          content: (
            <TelephonyLineRoutingTab
              line={line}
              isReadOnly={isReadOnly}
              data-testid={testIds.settings.telephonyLineDetail('routing')}
            />
          ),
        },
      ]}
    />
  );
}
